import axios from "axios"
import { toSearchParams } from "helpers/form"
import { pick } from "lodash"
import { receiveErrors, resetErrors } from "modules/errors/reducer"
import { receiveSuccess } from "modules/success/reducer"
import { errorsShownSelector } from "modules/errors/selectors"
import { loggedOut } from "store/auth"
import API from "constants/api"
import { isEmbeddedContext } from "helpers/embedded"

export const authHeader = () => {
  // Embedded (third-party widget iframe) must never send a logged-in user's token from the
  // shared same-origin localStorage — only the guest booking session token.
  const token = isEmbeddedContext()
    ? localStorage.getItem("guest_token") || ""
    : localStorage.getItem("assume_access_token") || localStorage.getItem("access_token") || localStorage.getItem("guest_token") || ""
  const headers = { "X-Device-Type": "web" }
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

// The request's headers: none with noAuth (opting out of Authorization entirely), the given guest
// token, or the session's auth headers; plus any extra headers.
const buildRequestHeaders = ({ noAuth, guestToken, extraHeaders }) => {
  let headers
  if (noAuth) {
    headers = undefined
  } else if (guestToken) {
    headers = { Authorization: `Bearer ${guestToken}` }
  } else {
    headers = authHeader()
  }
  return extraHeaders ? { ...headers, ...extraHeaders } : headers
}

export const api = (config) => async (dispatch, getState) => {
  const {
    url,
    method = "get",
    data,
    guestToken,
    params,
    paramsOptions,
    onStart,
    onSuccess,
    onError,
    props = {},
    refresh = true,
    noAuth = false,
    // Axios upload-progress callback, for multipart requests that want a real progress bar.
    onUploadProgress,
    // Extra request headers, added to the auth headers (e.g. the booking widget's Cart-Token).
    headers: extraHeaders,
    // Axios responseType, e.g. "blob" for file downloads. Defaults to JSON.
    responseType,
    suppressErrorNotification = false,
    showSuccessNotification = false
  } = config
  if (onStart) {
    const res = await dispatch({ type: onStart, props })
    if (res.abort) return
  }

  return new Promise(async (resolve, reject) => {
    try {
      const defaultParams = toSearchParams(API.DEFAULT_PARAMS)
      const serializedParams = toSearchParams(params, paramsOptions, defaultParams)
      const headers = buildRequestHeaders({ noAuth, guestToken, extraHeaders })
      const response = await axios.request({
        baseURL: API.BASE_URL,
        params: serializedParams,
        url,
        method,
        data,
        headers,
        onUploadProgress,
        responseType
      })
      const state = getState()
      const hasErrors = errorsShownSelector(state)
      if (hasErrors) dispatch(resetErrors())
      if (onSuccess) dispatch({ type: onSuccess, payload: response.data, props })
      if (showSuccessNotification) dispatch(receiveSuccess({ data: response.data }))
      if (response.data.guest_token) {
        localStorage.setItem("guest_token", response.data.guest_token)
      }
      resolve(response.data)
    } catch (error) {
      if (
        refresh &&
        !isEmbeddedContext() &&
        config.onSuccess !== loggedOut.type &&
        error?.response?.status === 401 &&
        (localStorage.getItem("access_token") || localStorage.getItem("refresh_token"))
      )
        // The retry's result must be piped back through resolve/reject — returning it from a
        // promise executor discards it, leaving the caller awaiting a promise that never settles.
        return tokenRefresh()
          .then(() => dispatch(api({ ...config, refresh: false })))
          .then(resolve)
          .catch(reject)

      const payload = {
        ...pick(error.response || {}, ["data", "status", "statusText"]),
        message: error.toString(),
        method,
        url
      }
      if (!suppressErrorNotification) {
        dispatch({ type: receiveErrors.type, payload, props })
      }
      if (onError) dispatch({ type: onError, payload, props })
      reject(payload)
    }
  })
}

const tokenRefresh = async () => {
  const assumeRefreshToken = localStorage.getItem("assume_refresh_token")
  const refreshToken = assumeRefreshToken || localStorage.getItem("refresh_token")
  const prefix = assumeRefreshToken ? "assume_" : ""
  return new Promise(async (resolve, reject) => {
    try {
      const response = await axios.post(
        API.REFRESH_TOKEN_URL,
        {
          refresh_token: refreshToken,
          grant_type: "refresh_token"
        },
        authHeader()
      )
      localStorage.setItem(`${prefix}access_token`, response.data.access_token)
      localStorage.setItem(`${prefix}refresh_token`, response.data.refresh_token)
      if (response.data.guest_token) {
        localStorage.setItem("guest_token", response.data.guest_token)
      }
      resolve(response)
    } catch (error) {
      localStorage.removeItem(`${prefix}access_token`)
      localStorage.removeItem(`${prefix}refresh_token`)
      localStorage.removeItem(`${prefix}redirect_url`)
      if (
        error.response?.status === 400 &&
        error.response.data?.error === "invalid_grant"
      ) {
        window.location.reload()
      }
      reject(error)
    }
  })
}
